// apps/web/src/pages/author/sections/IngredientGroups.tsx
//
// Phase 8 Plan 08 Task 2 — IngredientGroups form section (authr-01 / authr-03).
//
// Nested useFieldArray section:
//   recipe.ingredientGroups: IngredientGroup[]   (top-level array)
//     each group: { label: string; items: Ingredient[] }
//       items: Ingredient[]                       (nested array)
//         each item: { name, quantity?, unit?, note? }
//
// authr-03 contract: every useFieldArray row uses `field.id` as React key —
// NEVER index. The Plan 01 grep gate (scripts/check-fieldarray-keys.sh)
// enforces this statically; the FieldArrayRow.browser.test.tsx proves the
// runtime invariant.
//
// authr-06 hygiene: this file uses per-path useWatch ONLY (for the Dialog
// body interpolation in the parent group row); NEVER top-level useWatch,
// NEVER reads formState.*.
//
// W13 — SCHEMA-vs-UI-SPEC reconciliation:
//   UI-SPEC §"Destructive confirmations" line 371 reads:
//     "{group.name} and all ingredients inside it will be removed."
//   But the IngredientGroup schema (packages/shared/src/schemas/ingredient-
//   group.ts line 25) defines the field as `label` (META-04 free-form). Schema
//   is the single source of truth — we interpolate `{group.label}` instead.
//   Documented inline near the interpolation call site below.

import type { IngredientGroup, RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FieldArrayRow } from "../FieldArrayRow";

// Default values for a brand-new ingredient group. Items must be non-empty
// (IngredientGroup.items.min(1)) — seed one blank ingredient row.
const NEW_GROUP_DEFAULTS: IngredientGroup = { label: "", items: [{ name: "" }] };

// Default values for a brand-new ingredient inside an existing group.
const NEW_INGREDIENT_DEFAULTS = { name: "" };

export function IngredientGroups(): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "ingredientGroups",
  });

  return (
    <Fieldset legend="INGREDIENTS">
      {fields.length === 0 ? (
        <EmptyState
          title="No ingredients yet."
          cta={{
            label: "Add ingredient",
            onClick: () => append(NEW_GROUP_DEFAULTS),
          }}
        />
      ) : (
        <>
          {fields.map((field, index) => (
            <IngredientGroupRow
              key={field.id}
              index={index}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
            />
          ))}
          <button
            type="button"
            onClick={() => append(NEW_GROUP_DEFAULTS)}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Add ingredient group
          </button>
        </>
      )}
    </Fieldset>
  );
}
IngredientGroups.displayName = "IngredientGroups";

interface IngredientGroupRowProps {
  index: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function IngredientGroupRow({
  index,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
}: IngredientGroupRowProps): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  // W13: schema uses 'label' (META-04 free-form); UI-SPEC line 371 wording
  // reads {group.name} but schema is source of truth.
  // Per-path useWatch — narrow subscription to a single string field. Pitfall
  // 1 + 2 hygiene preserved.
  const groupLabel = useWatch({
    control,
    name: `ingredientGroups.${index}.label`,
  });
  const labelForDialog =
    typeof groupLabel === "string" && groupLabel.length > 0 ? groupLabel : `Group ${index + 1}`;

  return (
    <FieldArrayRow
      singular="ingredient group"
      index={index}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
      deleteDialogTitle="Delete ingredient group?"
      deleteDialogBody={`${labelForDialog} and all ingredients inside it will be removed.`}
      deleteConfirmLabel="Delete group"
    >
      <Fieldset legend={labelForDialog}>
        <FormField
          control={control}
          name={`ingredientGroups.${index}.label`}
          render={({ field }) => (
            <FormItem data-field-path={`ingredientGroups.${index}.label`}>
              <FormLabel>Group label</FormLabel>
              <FormControl>
                <Input {...field} autoFocus={!field.value} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <IngredientItems parentIndex={index} />
      </Fieldset>
    </FieldArrayRow>
  );
}

interface IngredientItemsProps {
  parentIndex: number;
}

function IngredientItems({ parentIndex }: IngredientItemsProps): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `ingredientGroups.${parentIndex}.items`,
  });

  return (
    <Fieldset legend="Items">
      {fields.length === 0 ? (
        <EmptyState
          title="No ingredients yet."
          cta={{
            label: "Add ingredient",
            onClick: () => append(NEW_INGREDIENT_DEFAULTS),
          }}
        />
      ) : (
        <>
          {fields.map((field, itemIndex) => (
            <FieldArrayRow
              key={field.id}
              singular="ingredient"
              index={itemIndex}
              canMoveUp={itemIndex > 0}
              canMoveDown={itemIndex < fields.length - 1}
              onMoveUp={() => move(itemIndex, itemIndex - 1)}
              onMoveDown={() => move(itemIndex, itemIndex + 1)}
              onRemove={() => remove(itemIndex)}
              deleteDialogTitle="Delete ingredient?"
              deleteDialogBody="This ingredient will be removed from the group."
              deleteConfirmLabel="Delete ingredient"
            >
              <FormField
                control={control}
                name={`ingredientGroups.${parentIndex}.items.${itemIndex}.name`}
                render={({ field }) => (
                  <FormItem
                    data-field-path={`ingredientGroups.${parentIndex}.items.${itemIndex}.name`}
                  >
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} autoFocus={!field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`ingredientGroups.${parentIndex}.items.${itemIndex}.quantity`}
                render={({ field }) => (
                  <FormItem
                    data-field-path={`ingredientGroups.${parentIndex}.items.${itemIndex}.quantity`}
                  >
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          field.onChange(raw === "" ? undefined : Number(raw));
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`ingredientGroups.${parentIndex}.items.${itemIndex}.unit`}
                render={({ field }) => (
                  <FormItem
                    data-field-path={`ingredientGroups.${parentIndex}.items.${itemIndex}.unit`}
                  >
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`ingredientGroups.${parentIndex}.items.${itemIndex}.note`}
                render={({ field }) => (
                  <FormItem
                    data-field-path={`ingredientGroups.${parentIndex}.items.${itemIndex}.note`}
                  >
                    <FormLabel>Note</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FieldArrayRow>
          ))}
          <button
            type="button"
            onClick={() => append(NEW_INGREDIENT_DEFAULTS)}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Add ingredient
          </button>
        </>
      )}
    </Fieldset>
  );
}
